/**
 * @author Antoine Laviron (alaviron@salesforce.com)
 * @date 2024-08-27
 *
 * @description
 * This component handles the OAuth 2.0 callback from Salesforce. After a user
 * successfully authenticates and authorizes the application, Salesforce redirects
 * them to this page. This component's sole responsibility is to extract the
 * authorization `code` from the URL, retrieve the `code_verifier` from session
 * storage, and send both to the backend to be exchanged for an access token.
 */

import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Loading from '../components/Loading';

function Callback() {
    // State to store and display any potential errors during the authentication process.
    const [error, setError] = useState(null);
    // Hook for programmatic navigation after a successful or failed authentication attempt.
    const navigate = useNavigate();
    // Flag to prevent double execution
    const hasInitiatedAuth = useRef(false); 

    useEffect(() => {
        // Immediately exit the effect if the authentication has already been triggered
        // in this component's lifecycle.
        if (hasInitiatedAuth.current) {
            return;
        }
        
        // Set the flag to true to prevent subsequent executions.
        hasInitiatedAuth.current = true;

        /**
         * Orchestrates the entire token exchange process.
         */        
        const authenticate = async () => {
            // --- Extract Authorization Code and Session Data ---
            // 1. Parse the authorization `code` from the current URL's query parameters.
            const urlParams = new URLSearchParams(window.location.search);
            const authCode = urlParams.get('code');

            if (!authCode) {
                setError('Authorization code not found in URL. Please try logging in again.');
                return; // Halt execution if the code is missing.
            }

            // 2. Retrieve the PKCE `code_verifier` and `client_id` from sessionStorage.
            //    These were stored right before redirecting the user to Salesforce on the Login page.
            const codeVerifier = sessionStorage.getItem('code_verifier');
            const clientId = sessionStorage.getItem('client_id');

            if (!codeVerifier || !clientId) {
                setError('Session data missing. Please try logging in again.');
                return; // Halt execution if session data is missing.
            }

            try {
                // --- Call Backend for Token Exchange ---
                // 3. Construct the request body for the backend's /api/auth endpoint.
                const requestBody = {
                    code: authCode,
                    client_id: clientId,
                    redirect_uri: `${window.location.origin}/callback`,
                    code_verifier: codeVerifier,
                };

                // 4. Send the data to the backend. The backend will handle the secure
                //    exchange of the code for an access token with Salesforce.                
                const response = await fetch('/api/auth', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(requestBody),
                });

                const data = await response.json();

                // If the response from the backend is not successful, parse the error
                // message and throw an error to be caught by the catch block.                
                if (!response.ok) {
                    const errorMessage = data.detail[0]?.msg || data.detail || 'Authentication failed';
                    throw new Error(errorMessage);
                }

                // --- Store Authentication Artifacts ---
                // 5. Upon success, store the received frontdoor and instance URLs in localStorage
                //    for persistent access across the application session.                
                localStorage.setItem('frontdoor_url', data.frontdoor_url);
                localStorage.setItem('instance_url', data.instance_url);
                
                // --- Cleanup and Redirect ---
                // 6. Remove the sensitive session data after it has been successfully used.
                sessionStorage.removeItem('code_verifier');
                sessionStorage.removeItem('client_id');

                // 7. Navigate the user to the main application dashboard.
                navigate('/salescloud');

            } catch (err) {
                // If any part of the try block fails, update the error state to display
                // a user-friendly message.
                setError(`An error occurred during authentication: ${err.message}`);
            }
        };

        // Execute the authentication function.
        authenticate();
    }, [navigate]);

    // If an error has occurred, render the error message instead of the loading spinner.
    if (error) {
        return <div className="container error-message">{error}</div>;
    }

    // By default, display a loading component to inform the user that the process is ongoing.
    return <Loading message="Finalizing login..." />;
}

export default Callback;

